import Ext from "Ext"
import $ from "jquery"
import View from "../../View"
import UsersSelectorPanel from "../../usersSelector/at_users_selector_panel"
import SharedUserButtonMenu from "../../controls/selectorControls/at_shared_user_button_menu"
import TwoLevelSelection from "../../controls/selectorControls/two_level_selection"
import GroupType from "../../enums/GroupType"
import UserNodeRenderer from "../../hierarchySelector/userSelector/UserNodeRenderer"
import UserGroupWithoutCheckboxNodeRenderer from "./UserGroupWithoutCheckboxNodeRenderer"
import TimeZoneGroupWithoutCheckboxNodeRenderer from "./TimeZoneGroupWithoutCheckboxNodeRenderer"
import DisabledUsersParentNodeRendererWithoutCheckbox from "./DisabledUsersParentNodeRendererWithoutCheckbox"
import PermissionIcons from "./PermissionIcons"
import {escapeHtmlSymbols, unorderedArraysDifference} from "../../atUtils"
import {extractPermissionsFromUser, getTitleOfGenerateMoneyReportsRight, getTitleOfModifyTimeTrackRight} from "./utils"
import TriStateSelectionController from "../../hierarchySelector/tree/TriStateSelectionController"
import TreeNode from "../../hierarchySelector/tree/TreeNode"
import {getInstance as assignmentsTooltip} from '../../SimpleSingletonTooltip'
import {onKeyDown} from '../../keyHandlers'
import {KEY_CODE_ESC} from '../../keyCodes'

export class NodeRenderer extends UserNodeRenderer {
    constructor({
        selectorTree,
        selectorFilter,
        selectionHelper,
        showPermissionIcons,
        canDeselectUser = user => true,
        showGenerateMoneyReportsIcon = false,
        generateMoneyReportsIconTooltip = "",
        modifyTimeTrackTooltipText = "",
        getDisabledTooltipText = user => ""
    }) {
        super(selectorTree, selectorFilter, selectionHelper)
        this.showPermissionIcons = showPermissionIcons
        this.canDeselectUser = canDeselectUser;
        this.showGenerateMoneyReportsIcon = showGenerateMoneyReportsIcon;
        this.generateMoneyReportsIconTooltip = generateMoneyReportsIconTooltip
        this.modifyTimeTrackTooltipText = modifyTimeTrackTooltipText
        this.getDisabledTooltipText = getDisabledTooltipText
        this.unsubscribeTooltipMap = {}
    }

    generateContent(treeNode, targetEl) {
        super.generateContent(treeNode, targetEl);

        const user = treeNode.getValue();
        if (this.showPermissionIcons) {
            new PermissionIcons({
                ...extractPermissionsFromUser(user),
                showCostReports: this.showGenerateMoneyReportsIcon,
                costReportsTooltipText: this.generateMoneyReportsIconTooltip,
                modifyTimeTrackTooltipText: this.modifyTimeTrackTooltipText,
            }).init($(targetEl));
        }

        if (!this.canDeselectUser(user)) {
            const disabledTooltipText = this.getDisabledTooltipText(user)
            this.unsubscribeTooltipMap[user.id] && this.unsubscribeTooltipMap[user.id]()
            if (disabledTooltipText) {
                const $checkbox = $(targetEl).find('.checkbox')
                this.unsubscribeTooltipMap[user.id] = assignmentsTooltip().subscribeToMouseEvents($checkbox, () => {
                    assignmentsTooltip().setState({
                        text: disabledTooltipText,
                        extraClass: 'hierarchyAssignmentsTooltip',
                    })
                })
            }
        }
    }
}

export const SelectorPanel = Ext.extend(UsersSelectorPanel, {
    showPermissionIcons: false,
    canDeselectUser: user => true,
    title: "",
    showGenerateMoneyReportsIcon: false,
    generateMoneyReportsIconTooltip: "",
    getDisabledTooltipText: user => "",

    createTree() {
        const tree = SelectorPanel.superclass.createTree.call(this);
        const alwaysSelectedNodes = tree.getAllNodes().filter(node => node.getType() === TreeNode.TYPE_USER && !this.canDeselectUser(node.getValue()))
        tree.selectionController = new TriStateSelectionController(tree, alwaysSelectedNodes);
        return tree;
    },

    createComponents() {
        SelectorPanel.superclass.createComponents.call(this);
        this.addClass("assignedUserSelector");
        if (this.showPermissionIcons) {
            this.addClass("withPermissionIcons");
        }
    },

    getUserNodeRenderer() {
        return new NodeRenderer({
            selectorTree: this.selectorTree,
            selectorFilter: this.selectorFilter,
            selectionHelper: this.getSelectionHelper(),
            showPermissionIcons: this.showPermissionIcons,
            showGenerateMoneyReportsIcon: this.showGenerateMoneyReportsIcon,
            generateMoneyReportsIconTooltip: this.generateMoneyReportsIconTooltip,
            modifyTimeTrackTooltipText: this.modifyTimeTrackTooltipText,
            getDisabledTooltipText: this.getDisabledTooltipText,
            canDeselectUser: this.canDeselectUser,
        });
    },

    getUserGroupNodeRenderer: function() {
        return new UserGroupWithoutCheckboxNodeRenderer(this.selectorTree, this.selectorFilter, this.getExpandCollapseCallback());
    },

    getTimeZoneGroupNodeRenderer: function() {
        return new TimeZoneGroupWithoutCheckboxNodeRenderer(this.selectorTree, this.selectorFilter, this.getExpandCollapseCallback());
    },

    getDisabledUsersParentNodeRenderer: function() {
        return new DisabledUsersParentNodeRendererWithoutCheckbox( this.selectorTree, this.selectorFilter, this.getExpandCollapseCallback() );
    },

    createGenericOptions() {
        this.genericOptions = [];
    },

    createCustomOption() {
    },

    getGenericOptionsSection() {
        return [];
    },

    getCustomOptionSection() {
        const toolbox = new Ext.Container({
            cls: "userSelectorCustomControlsContainer",
            autoEl: "div",
            items: [
                this.viewByPanel,
                this.getCustomOptionButtonsSection()
            ]
        });
        if (this.title) {
            const titleLabel = {
                xtype: "box",
                cls: "panelTitle",
                autoEl: {
                    tag: "div",
                    html: escapeHtmlSymbols(this.title)
                }
            };
            return [titleLabel, toolbox];
        } else {
            return [toolbox];
        }
    },

    getDetailsSection() {
        return [this.selectorTree, this.showInactiveCheckboxContainer];
    },

    getCurrentOptionValue() {
        return this.customOptionValue;
    },

    isCustomOptionSelected() {
        return true;
    },

    mapSelectionGroupsToUsers() {
        return true;
    }
})

export default class UsersSelector extends View {
    constructor({
        selectionCallback = ({addedUserIds, removedUserIds}) => {},
        showPermissionIcons = false,
        canDeselectUser = user => true,
        resetOnHide = true,
        parentEl = null,
        refreshScrollOnShowCb = () => {},
        refreshScrollOnHideCb = () => {},
        getDisabledTooltipText = user => "",
    }) {
        super();

        this.params = {
            showPermissionIcons,
            resetOnHide,
            parentEl,
            selectionCallback,
            canDeselectUser,
            refreshScrollOnShowCb,
            refreshScrollOnHideCb,
            getDisabledTooltipText,
        }

        this.state = {
            selection: [],
            users: [],
            userGroups: [],
            groupBy: GroupType.USER_GROUP,
            timeZoneGroups: [],
            userGroupsEnabled: true,
            timeZoneGroupsEnabled: true,
            title: "",
            isURAEnabled: false,
            isBTYEnabled: false,
            isTTAEnabled: false,
        }

        this.onStateDeeplyChanged(() => this._initPanel());
    }

    showAt($el, position) {
        this.menu.show($el[0], position);
    }

    hide() {
        this.menu.hide();
    }

    _initPanel() {
        const panel = new SelectorPanel({
            showPermissionIcons: this.params.showPermissionIcons,
            canDeselectUser: this.params.canDeselectUser,
            title: this.state.title,
            showGenerateMoneyReportsIcon: this.state.isBTYEnabled || this.state.isURAEnabled,
            generateMoneyReportsIconTooltip: getTitleOfGenerateMoneyReportsRight(this.state.isBTYEnabled, this.state.isURAEnabled),
            modifyTimeTrackTooltipText: getTitleOfModifyTimeTrackRight(this.state.isTTAEnabled),
            resetOnHide: this.params.resetOnHide,
            showShowInactiveCheckbox: false,
            getDisabledTooltipText: this.params.getDisabledTooltipText,
        });
        panel.initialize(
                this.state.userGroupsEnabled,
                this.state.timeZoneGroupsEnabled,
                this.state.users,
                this.state.userGroups,
                this.state.timeZoneGroups
        );
        panel.loadState({
            selection: new TwoLevelSelection([], this.state.selection),
            groupType: this.state.groupBy,
            additionalCheckBoxValue: false,
            optionValue: false,
            showInactive: false
        });

        const menu = new SharedUserButtonMenu({
            panel,
            parentEl: this.params.parentEl,
        })
        this._onKeyDownRemover = null
        menu.on("hide", () => {
            const selection = panel.getSelection().getSecondLevel();
            const {added: addedUserIds, removed: removedUserIds} = unorderedArraysDifference(this.state.selection, selection);
            if (addedUserIds.length > 0 || removedUserIds.length > 0) {
                this.params.selectionCallback({addedUserIds, removedUserIds});
            }
            if (this.params.resetOnHide) {
                panel.resetNodes();
            }
            this.params.refreshScrollOnHideCb()

            if(this._onKeyDownRemover) this._onKeyDownRemover()
        });
        menu.on("show", () => {
            this.params.refreshScrollOnShowCb(menu.el.getBottom())
            this._onKeyDownRemover = onKeyDown((code) => {
                // handle event for components that contains this selector
                if(code === KEY_CODE_ESC) return true
            })
        })

        this.menu = menu;
    }
}



// WEBPACK FOOTER //
// ./web/js/panels/usersAssignedToHierarchy/UsersSelector.js